import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import cloneDeep from 'lodash.clonedeep';
import { map, Observable, of, switchMap, takeUntil, tap } from 'rxjs';
import { IMaskConstants } from '../../../../core/constants/imask';
import { NgxDateConstants } from '../../../../core/constants/ngx';
import {
    formatDateToMMDDYYYY,
    formatToDate,
} from '../../../../core/functions/date-helpers';
import { hasOpenDialogs } from '../../../../core/functions/dialog-helper';
import {
    nameOf,
    tryParseToNumber,
} from '../../../../core/functions/string-helpers';
import { BlockNavigationIfChange } from '../../../../core/models/block-navigation-if-change';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { FormInputCellRendererParams } from '../../../../core/models/grid/form-input-cell-renderer-params';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { ActionCode } from '../../../../core/models/resources/action-code';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { ApplicationFormFieldComponent } from '../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { ApplicationPageBaseComponent } from '../../../../shared/components/fcc-601/application-page-base/application-page-base.component';
import { GridToolbarComponent } from '../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { GridToolbarOptions } from '../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { AttachmentRequiredButtonComponent } from '../shared/components/attachment/attachment-required-button/attachment-required-button.component';
import { ApplicationPageFacadeService } from '../shared/services/application-page-facade/application-page-facade.service';
import { DirectEntryService } from '../shared/services/direct-entry/direct-entry.service';
import { ScheduleKAttachmentDialogComponent } from './components/schedule-k-attachment-dialog/schedule-k-attachment-dialog.component';
import { ScheduleKConstants } from './constants/schedule-k-constants';
import { ScheduleKFormModel } from './models/schedule-k-form-model';
import { ScheduleKFrequencyBaseModel } from './models/schedule-k-frequency-base-model';
import { ScheduleKFrequencyFormModel } from './models/schedule-k-frequency-form-model';
import { ScheduleKRequest } from './models/schedule-k-request';
import { ScheduleKResponse } from './models/schedule-k-response';
import { ScheduleKService } from './services/schedule-k.service';

@Component({
    selector: 'app-schedule-k',
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        AgGridAngular,
        ApplicationFormFieldComponent,
        GridToolbarComponent,
        AttachmentRequiredButtonComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './schedule-k.component.html',
})
export class ScheduleKComponent
    extends ApplicationPageBaseComponent<
        ScheduleKRequest,
        ScheduleKResponse,
        ScheduleKFormModel
    >
    implements OnInit, OnDestroy, BlockNavigationIfChange
{
    private readonly directEntryService = inject(DirectEntryService);
    private readonly dropdownService = inject(DropdownService);
    private readonly scheduleKService = inject(ScheduleKService);
    private readonly dialog = inject(MatDialog);

    purposeOfSubmissionOptions: DropdownOption[] = [];

    constructor() {
        super(
            inject(ActivatedRoute),
            inject(Router),
            inject(ApplicationPageFacadeService),
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadDropdownData();
    }

    get scheduleKFormFrequencies(): FormArray<
        FormGroup<FormGroupType<ScheduleKFrequencyFormModel>>
    > {
        return this.form.controls.scheduleKFrequencies as FormArray<
            FormGroup<FormGroupType<ScheduleKFrequencyFormModel>>
        >;
    }

    override form = this.scheduleKService.getFormDefinition();

    gridApi: GridApi | undefined;
    rowData: ScheduleKFrequencyFormModel[] = [];
    scheduleKResponse: ScheduleKResponse | undefined;
    shouldDisplayAttachmentWarningFlag: boolean = false;

    columnDefs: ColDef[] = [
        {
            headerName: 'Location Number',
            field: nameOf<ScheduleKFrequencyFormModel>('locationNumber'),
            wrapHeaderText: true,
            filter: true,
        },
        {
            headerName: 'Center or Lower Frequency',
            field: nameOf<ScheduleKFrequencyFormModel>('frequencyLow'),
            wrapHeaderText: true,
            filter: true,
            cellDataType: 'number',
            minWidth: 200,
            filterParams: {
                ...GridHelper.defaultNumericFilterParams,
                filterPlaceholder: 'Lower Frequency',
            },
        },
        {
            headerName: 'Upper Frequency',
            field: nameOf<ScheduleKFrequencyFormModel>('frequencyHigh'),
            wrapHeaderText: true,
            filter: true,
            cellDataType: 'number',
            filterParams: {
                ...GridHelper.defaultNumericFilterParams,
                filterPlaceholder: 'Upper Frequency',
            },
        },
        {
            headerName: 'Construct by Date',
            field: nameOf<ScheduleKFrequencyFormModel>(
                'constructionDeadlineDate',
            ),
            wrapHeaderText: true,
            filter: true,
            ...GridHelper.defaultFormDateRendererColDef,
            cellDataType: 'date',
            filterParams: {
                ...GridHelper.defaultDateFilterParams,
            },
        },
        {
            field: nameOf<ScheduleKFrequencyFormModel>(
                'actualDateOfConstruction',
            ),
            headerName: 'Actual Date of Construction',
            wrapHeaderText: true,
            filter: true,
            minWidth: 200,
            ...GridHelper.defaultFormInputCellRendererColDef,
            ...GridHelper.defaultFormDateRendererColDef,
            cellRendererParams: {
                title: 'Actual Date of Construction',
                required: false,
                placeholder: NgxDateConstants.PLACEHOLDER,
                classList: 'text-center',
                type: 'text',
                mask: {
                    showMaskTyped: NgxDateConstants.SHOW_MASK_TYPED,
                    specialCharacters: NgxDateConstants.SPECIAL_CHARACTERS,
                    dropSpecialCharacters:
                        NgxDateConstants.DROP_SPECIAL_CHARACTERS,
                    placeHolderCharacter:
                        NgxDateConstants.PLACE_HOLDER_CHARACTER,
                    shownMaskExpression: NgxDateConstants.SHOWN_MASK_EXPRESSION,
                    leadZeroDateTime: NgxDateConstants.LEAD_ZERO_DATE_TIME,
                    mask: NgxDateConstants.MASK,
                },
                onFocusout: (params: FormControl) => {
                    this.updateActionCode(params);
                    this.setIsAttachmentRequired();
                },
            } as FormInputCellRendererParams,
            cellDataType: 'date',
            filterParams: {
                ...GridHelper.defaultDateFilterParams,
            },
        },
        {
            field: nameOf<ScheduleKFrequencyFormModel>('numberOfMobiles'),
            headerName: 'Number of Mobiles',
            wrapHeaderText: true,
            filter: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            headerTooltip: ScheduleKConstants.NumberOfMobilesTooltip,
            cellRendererParams: {
                required: true,
                type: 'text',
                classList: 'text-center',
                onFocusout: (params: FormControl) => {
                    this.updateActionCode(params);
                },
                imask: IMaskConstants.positiveIntegers,
            } as FormInputCellRendererParams,
        },
    ];

    gridOptions: GridOptions = {
        ...GridHelper.defaultFormFieldGridOptions,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
        columnDefs: this.columnDefs,
        getRowId: GridHelper.defaultGetRowId,
    };

    gridToolbarOptions: GridToolbarOptions = {
        refreshDataForExport: () => this.updateGrid(),
        quickFilter: false,
        clearFilters: true,
        exportCsv: true,
        exportCsvOptions: {
            fileName: () =>
                GridHelper.getExportFileName(
                    'BuildOut_Frequency',
                    this.applicationState?.salmonLogId,
                ),
        },
    };

    protected override bindForm(response: ScheduleKResponse): void {
        this.scheduleKResponse = cloneDeep(response);
        const scheduleKFormFrequencies: ScheduleKFrequencyFormModel[] = [];

        response.scheduleKFrequencies.forEach((freq) => {
            const formFreq: ScheduleKFrequencyFormModel = {
                ...freq,
                actualDateOfConstruction: formatDateToMMDDYYYY(
                    freq.actualDateOfConstruction,
                ),
                numberOfMobiles: freq.numberOfMobiles?.toString() ?? '',
            };

            scheduleKFormFrequencies.push(formFreq);
            this.scheduleKFormFrequencies.push(
                this.scheduleKService.getScheduleKFrequencyFormDefinition(),
            );
        });

        this.rowData = scheduleKFormFrequencies;

        const formValues: ScheduleKFormModel = {
            ...response,
            scheduleKFrequencies: scheduleKFormFrequencies,
        };

        this.form.patchValue(formValues);

        const currentNotificationCode =
            this.form.controls.notificationCode.value;
        if (
            currentNotificationCode === null ||
            currentNotificationCode === ''
        ) {
            this.form.patchValue({
                notificationCode:
                    ScheduleKConstants.DefaultNotificationCodeValue,
            });
            this.form.controls.notificationCode.markAsDirty();
        }

        this.scheduleKFormFrequencies.controls.forEach((dataRow) => {
            this.handleRowFieldDisabling(dataRow);
        });

        this.setIsAttachmentRequired();
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<ScheduleKResponse> {
        return this.directEntryService.getScheduleK(applicationId);
    }

    protected override saveFormDataToApi(
        request: ScheduleKRequest,
    ): Observable<StepValidationResult> {
        // Display a warning modal if an attachment is required
        const resolveRequest$ = this.shouldDisplayAttachmentWarningFlag
            ? this.displayAttachmentWarningModal().pipe(map(() => request))
            : of(request);

        return resolveRequest$.pipe(
            switchMap((resolvedRequest) =>
                this.directEntryService.saveScheduleK(resolvedRequest),
            ),
        );
    }

    protected override generateRequestObject(): ScheduleKRequest {
        const formValue =
            this.form.getRawValue() as Required<ScheduleKFormModel>;

        const scheduleKResponse: ScheduleKResponse = {
            notificationCode: formValue.notificationCode,
            callSign: formValue.callSign,
            scheduleKFrequencies: new Array<ScheduleKFrequencyBaseModel>(),
        };

        formValue.scheduleKFrequencies.forEach((freq) => {
            scheduleKResponse.scheduleKFrequencies.push({
                ...freq,
                numberOfMobiles: tryParseToNumber(freq.numberOfMobiles),
                // Clean up nullable fields: Angular forms convert empty inputs to empty strings (""),
                // but .NET's JSON deserializer expects null for nullable types (int?, DateTime?).
                // Empty strings cause the entire request deserialization to fail.
                actualDateOfConstruction: formatToDate(
                    freq.actualDateOfConstruction,
                ),
            });
        });

        return {
            ...scheduleKResponse,
            applicationId: this.applicationId!,
        };
    }

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.gridApi.setGridOption('rowData', this.rowData);
    }

    updateGrid() {
        const data = cloneDeep(this.scheduleKFormFrequencies.value);
        this.gridApi?.setGridOption('rowData', data);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initializeGridContext(): any {
        return {
            formArray: this.scheduleKFormFrequencies,
        };
    }

    private loadDropdownData() {
        this.dropdownService
            .getPurposeOfSubmissionOptions()
            .pipe(
                takeUntil(this.destroy$),
                tap((options: DropdownOption[]) => {
                    this.purposeOfSubmissionOptions = options;
                }),
            )
            .subscribe();
    }

    private handleRowFieldDisabling(
        dataRow: FormGroup<FormGroupType<ScheduleKFrequencyFormModel>>,
    ): void {
        // Actual Date of Construction and Number of Mobiles are disabled if the Construct By Date is not populated, or if the data is prepopulated from ULS
        const constructionDeadlineDate =
            dataRow.controls.constructionDeadlineDate.value;
        const actualDateOfConstruction =
            dataRow.controls.actualDateOfConstruction.value;

        const isConstructionDeadlineDateEmpty =
            constructionDeadlineDate === null ||
            constructionDeadlineDate.toString() === '';
        const isActualDateOfConstructionPopulated =
            actualDateOfConstruction !== null &&
            actualDateOfConstruction.toString() !== '';

        if (
            isConstructionDeadlineDateEmpty ||
            (isActualDateOfConstructionPopulated &&
                dataRow.controls.actionCode.value !== ActionCode.Add)
        ) {
            this.applicationPageFacadeService.applicationFormControlManager.disable(
                dataRow.controls.actualDateOfConstruction,
                { emitEvent: false },
            );
            this.applicationPageFacadeService.applicationFormControlManager.disable(
                dataRow.controls.numberOfMobiles,
                { emitEvent: false },
            );
            return;
        }

        // Number of mobiles is only enabled if either frequencyLow or frequencyHigh is within the mobile range
        const frequencyLow = dataRow.controls.frequencyLow.value;
        const frequencyHigh = dataRow.controls.frequencyHigh.value;

        let frequencyLowWithinMobileRange = false;
        let frequencyHighWithinMobileRange = false;

        if (frequencyLow != null) {
            frequencyLowWithinMobileRange =
                this.scheduleKService.isFrequencyWithinMobileRange(
                    frequencyLow,
                );
        }

        if (frequencyHigh != null) {
            frequencyHighWithinMobileRange =
                this.scheduleKService.isFrequencyWithinMobileRange(
                    frequencyHigh,
                );
        }

        // FrequencyHigh is often not populated, check for either end of the range being within the mobile range
        if (frequencyLowWithinMobileRange || frequencyHighWithinMobileRange) {
            this.applicationPageFacadeService.applicationFormControlManager.enable(
                dataRow.controls.numberOfMobiles,
                { emitEvent: false },
            );
        } else {
            this.applicationPageFacadeService.applicationFormControlManager.disable(
                dataRow.controls.numberOfMobiles,
                { emitEvent: false },
                () => {
                    dataRow.controls.numberOfMobiles.setValue(null, {
                        emitEvent: false,
                    });
                },
            );
        }
    }

    /**
     * Set action code to 'Add' when data is modified. Required for proper validation between disabled and enabled fields.
     */
    private updateActionCode(control: FormControl): void {
        const dataRow = control.parent as FormGroup<
            FormGroupType<ScheduleKFrequencyFormModel>
        >;
        dataRow.controls.actionCode.setValue(ActionCode.Add);
    }

    private displayAttachmentWarningModal(): Observable<boolean> {
        if (hasOpenDialogs(this.dialog)) {
            return of(false);
        }

        const dialogRef = this.dialog.open(ScheduleKAttachmentDialogComponent, {
            disableClose: true,
        });

        return dialogRef.afterClosed() as Observable<boolean>;
    }

    private setIsAttachmentRequired(): void {
        this.shouldDisplayAttachmentWarningFlag =
            this.form.controls.scheduleKFrequencies.value.some(
                (dataRow: ScheduleKFrequencyFormModel) => {
                    // Ignore any pre-populated rows
                    if (
                        dataRow.actionCode === null ||
                        dataRow.actionCode === undefined
                    ) {
                        return false;
                    }
                    const actualDateOfConstruction = formatToDate(
                        dataRow.actualDateOfConstruction,
                    );
                    const constructionDeadlineDate =
                        dataRow.constructionDeadlineDate;

                    if (actualDateOfConstruction && constructionDeadlineDate) {
                        const today = new Date(Date.now());
                        if (actualDateOfConstruction > today) {
                            return false;
                        }

                        const gracePeriodDate = new Date(
                            constructionDeadlineDate,
                        );
                        gracePeriodDate.setDate(
                            gracePeriodDate.getDate() +
                                ScheduleKConstants.GracePeriodDays,
                        );

                        return (
                            today > gracePeriodDate ||
                            actualDateOfConstruction > constructionDeadlineDate
                        );
                    } else {
                        return false;
                    }
                },
            );
    }
}
